import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import microbiologyService from '../../../services/microbiologyService';
import { calculateCheckDigit, formatBarcodeWithCheckDigit } from '../../../utils/checkDigitUtil';
import CommonPagination from '../../../components/common/CommonPagination';
import usePagination from '../../../hooks/usePagination';
import './MicrobiologyMasters.css';

const SYMBOLOGY_OPTIONS = [
  { value: 'CODE128', label: 'Code 128 (High-Density 1D Linear - Standard)' },
  { value: 'CODE39', label: 'Code 39 (Standard 1D Alphanumeric)' },
  { value: 'DATAMATRIX_2D', label: 'DataMatrix 2D (Compact Square - Tube & Cryovial)' },
  { value: 'QR_CODE', label: 'QR Code (High Capacity 2D Matrix)' },
  { value: 'GS1_128', label: 'GS1-128 (Healthcare Global Standard Identifier)' },
  { value: 'AZTEC', label: 'Aztec 2D Code' }
];

const CHECK_DIGIT_ALGORITHMS = [
  { value: 'MOD10', label: 'Modulo 10 (Standard Check Digit)' },
  { value: 'LUHN', label: 'Luhn Algorithm (ISO/IEC 7812)' },
  { value: 'MOD11', label: 'Modulo 11 (Weighted Checksum)' },
  { value: 'ISO7064', label: 'ISO 7064 Mod 37, 36 (High Security)' },
  { value: 'NONE', label: 'None / Plain Sequence' }
];

const LABEL_SIZES = [
  { value: '50x25', label: '50 x 25 mm (Standard Primary Specimen Tube)' },
  { value: '40x20', label: '40 x 20 mm (Blood Culture & Culture Swab)' },
  { value: '35x15', label: '35 x 15 mm (Agar Petri Dish & Slide)' },
  { value: '25x10_CRYO', label: '25 x 10 mm (Cryovial / Microtube Cap)' },
  { value: '75x50', label: '75 x 50 mm (Specimen Bag / Shipping Container)' }
];

const AVAILABLE_HR_FIELDS = [
  { id: 'ACCESSION_NO', label: 'Accession / Lab No.' },
  { id: 'PATIENT_NAME', label: 'Patient Full Name' },
  { id: 'PATIENT_AGE_GENDER', label: 'Age / Gender' },
  { id: 'SPECIMEN_TYPE', label: 'Specimen Matrix' },
  { id: 'COLLECTION_DATETIME', label: 'Collection Date & Time' },
  { id: 'ISOLATE_NO', label: 'Isolate Number (e.g., #1, #2)' },
  { id: 'ORGANISM_CODE', label: 'Organism Code / Identification' },
  { id: 'DRAW_SITE', label: 'Anatomical Draw Site' },
  { id: 'WARD_BED', label: 'Ward / Bed / Department' }
];

const DEFAULT_FALLBACK_RULES = [
  {
    ruleID: 1,
    ruleCode: 'BC-001',
    ruleName: 'Standard Microbiology Specimen Tube Label',
    centreID: null,
    centreName: 'All Centers / Central Lab',
    specimenType: 'ALL',
    symbology: 'CODE128',
    identifierMapping: 'ACCESSION_SPECIMEN',
    prefix: 'MB',
    sequenceLength: 8,
    checkDigit: 'MOD10',
    labelSize: '50x25',
    printerType: 'ZEBRA_ZPL',
    printerDpi: '300_DPI',
    copies: 2,
    humanReadableFields: ['ACCESSION_NO', 'PATIENT_NAME', 'PATIENT_AGE_GENDER', 'SPECIMEN_TYPE', 'COLLECTION_DATETIME'],
    includeBiohazard: true,
    includeIsolateSuffix: false,
    isActive: true
  }
];

export default function BarcodeLabelConfigPage() {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSymbology, setFilterSymbology] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);

  const [formData, setFormData] = useState({
    ruleID: null,
    ruleCode: '',
    ruleName: '',
    centreID: null,
    centreName: 'All Centers / Central Lab',
    specimenType: 'ALL',
    symbology: 'CODE128',
    identifierMapping: 'ACCESSION_SPECIMEN',
    prefix: 'MB',
    sequenceLength: 8,
    checkDigit: 'MOD10',
    labelSize: '50x25',
    printerType: 'ZEBRA_ZPL',
    printerDpi: '300_DPI',
    copies: 2,
    humanReadableFields: ['ACCESSION_NO', 'PATIENT_NAME', 'SPECIMEN_TYPE', 'COLLECTION_DATETIME'],
    includeBiohazard: true,
    includeIsolateSuffix: false,
    isActive: true
  });

  const loadRules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await microbiologyService.getBarcodeRules();
      if (res && res.success && Array.isArray(res.data) && res.data.length > 0) {
        setConfigs(res.data);
        if (!previewItem && res.data.length > 0) {
          setPreviewItem(res.data[0]);
        }
      } else {
        setConfigs(DEFAULT_FALLBACK_RULES);
        if (!previewItem) setPreviewItem(DEFAULT_FALLBACK_RULES[0]);
      }
    } catch (err) {
      console.warn('Could not load barcode rules from backend API, using configured fallback matrix:', err);
      setConfigs(DEFAULT_FALLBACK_RULES);
      if (!previewItem) setPreviewItem(DEFAULT_FALLBACK_RULES[0]);
    } finally {
      setLoading(false);
    }
  }, [previewItem]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const filteredConfigs = useMemo(() => {
    return configs.filter(c => {
      const name = c.ruleName || '';
      const prefix = c.prefix || '';
      const center = c.centreName || '';
      const matchSearch = name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          prefix.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          center.toLowerCase().includes(searchQuery.toLowerCase());
      const matchSym = filterSymbology === 'ALL' || c.symbology === filterSymbology;
      const matchStat = filterStatus === 'ALL' || (filterStatus === 'Active' ? c.isActive : !c.isActive);
      return matchSearch && matchSym && matchStat;
    });
  }, [configs, searchQuery, filterSymbology, filterStatus]);

  const pagination = usePagination({
    items: filteredConfigs,
    defaultPageSize: 10,
    resetDeps: [searchQuery, filterSymbology, filterStatus]
  });

  const handleOpenCreate = () => {
    setEditingConfig(null);
    setFormData({
      ruleID: null,
      ruleCode: '',
      ruleName: '',
      centreID: null,
      centreName: 'All Centers / Central Lab',
      specimenType: 'ALL',
      symbology: 'CODE128',
      identifierMapping: 'ACCESSION_SPECIMEN',
      prefix: 'MB',
      sequenceLength: 8,
      checkDigit: 'MOD10',
      labelSize: '50x25',
      printerType: 'ZEBRA_ZPL',
      printerDpi: '300_DPI',
      copies: 2,
      humanReadableFields: ['ACCESSION_NO', 'PATIENT_NAME', 'SPECIMEN_TYPE', 'COLLECTION_DATETIME'],
      includeBiohazard: true,
      includeIsolateSuffix: false,
      isActive: true
    });
    setShowModal(true);
  };

  const handleOpenEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      ...config,
      humanReadableFields: config.humanReadableFields || ['ACCESSION_NO', 'PATIENT_NAME', 'SPECIMEN_TYPE', 'COLLECTION_DATETIME']
    });
    setShowModal(true);
  };

  const handleToggleHRField = (fieldId) => {
    setFormData(prev => {
      const current = prev.humanReadableFields || [];
      const updated = current.includes(fieldId)
        ? current.filter(f => f !== fieldId)
        : [...current, fieldId];
      return { ...prev, humanReadableFields: updated };
    });
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    if (!formData.ruleName.trim()) {
      alert('Please provide a descriptive Rule Name.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ruleID: editingConfig ? (editingConfig.ruleID || editingConfig.id) : null,
        ruleCode: formData.ruleCode || (editingConfig ? editingConfig.ruleCode : `BC-${String(configs.length + 1).padStart(3, '0')}`),
        ruleName: formData.ruleName,
        centreID: formData.centreID,
        centreName: formData.centreName,
        specimenType: formData.specimenType,
        symbology: formData.symbology,
        identifierMapping: formData.identifierMapping,
        prefix: formData.prefix,
        sequenceLength: formData.sequenceLength,
        checkDigit: formData.checkDigit,
        labelSize: formData.labelSize,
        printerType: formData.printerType,
        printerDpi: formData.printerDpi,
        copies: formData.copies,
        humanReadableFields: formData.humanReadableFields,
        includeBiohazard: formData.includeBiohazard,
        includeIsolateSuffix: formData.includeIsolateSuffix,
        isActive: formData.isActive
      };

      const res = await microbiologyService.saveBarcodeRule(payload);
      if (res && res.success) {
        await loadRules();
        setPreviewItem(payload);
      } else {
        // Fallback local update
        if (editingConfig) {
          setConfigs(configs.map(c => (c.ruleID === editingConfig.ruleID || c.id === editingConfig.id) ? { ...payload, ruleID: c.ruleID || c.id } : c));
          setPreviewItem(payload);
        } else {
          const newId = configs.length + 1;
          const newItem = { ...payload, ruleID: newId };
          setConfigs([newItem, ...configs]);
          setPreviewItem(newItem);
        }
      }
      setShowModal(false);
    } catch (err) {
      console.error('Error saving barcode rule:', err);
      // Fallback local update
      if (editingConfig) {
        setConfigs(configs.map(c => (c.ruleID === editingConfig.ruleID || c.id === editingConfig.id) ? { ...formData } : c));
      } else {
        setConfigs([{ ...formData, ruleID: configs.length + 1 }, ...configs]);
      }
      setShowModal(false);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (item) => {
    const updatedStatus = !item.isActive;
    try {
      await microbiologyService.saveBarcodeRule({
        ...item,
        isActive: updatedStatus
      });
      await loadRules();
    } catch (err) {
      setConfigs(configs.map(c => (c.ruleID === item.ruleID || c.id === item.id) ? { ...c, isActive: updatedStatus } : c));
    }
  };

  return (
    <div className="micro-master-container">
      {/* Header */}
      <div className="micro-header-section">
        <div className="micro-header-title">
          <div className="micro-breadcrumb">
            <span onClick={() => navigate('/masters/microbiology')} style={{ cursor: 'pointer', color: '#0284c7' }}>
              Microbiology Masters
            </span>
            <span> / </span>
            <span>Barcode & Label Configuration</span>
          </div>
          <h1>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#0284c7" strokeWidth="2.2">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="M6 8v8M9 8v5M12 8v8M15 8v3M18 8v8" />
            </svg>
            Barcode & Specimen Label Configuration
          </h1>
          <p>Database-persisted symbologies, accession mapping, check digits, thermal printer configurations & label sizing</p>
        </div>
        <div className="micro-header-actions">
          <button className="micro-btn-secondary" onClick={() => navigate('/masters/microbiology')}>
            &larr; Back to Masters Hub
          </button>
          <button className="micro-btn-primary" onClick={handleOpenCreate}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            + Create Barcode Rule
          </button>
        </div>
      </div>

      {/* Info Card */}
      <div className="micro-info-banner" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <span style={{ fontSize: '20px' }}>🏷️</span>
          <div>
            <strong style={{ color: '#166534', fontSize: '13px' }}>Persistent Specimen Machine-Readable Identifiers:</strong>
            <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: '#15803d' }}>
              Barcode rules are persisted directly to the laboratory database (`micro_barcode_rule`). Formats resolve deterministically across all laboratory branches and specimen collection stations.
            </p>
          </div>
        </div>
      </div>

      {/* Top Filter Bar */}
      <div className="micro-filter-bar">
        <div className="micro-search-box">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search rule name, prefix, or center..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="micro-filter-group">
          <label>Symbology:</label>
          <select value={filterSymbology} onChange={(e) => setFilterSymbology(e.target.value)}>
            <option value="ALL">All Symbologies</option>
            <option value="CODE128">Code 128</option>
            <option value="CODE39">Code 39</option>
            <option value="DATAMATRIX_2D">DataMatrix 2D</option>
            <option value="QR_CODE">QR Code</option>
            <option value="GS1_128">GS1-128</option>
          </select>
        </div>

        <div className="micro-filter-group">
          <label>Status:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="ALL">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Main Grid: Config Table + Live Label Preview */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '16px', alignItems: 'start' }}>
        {/* Left: Rules Table */}
        <div className="micro-table-card">
          {loading ? (
            <div style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>Loading barcode rules from database...</div>
          ) : (
            <table className="micro-table">
              <thead>
                <tr>
                  <th>Rule / Code</th>
                  <th>Center Scope</th>
                  <th>Specimen</th>
                  <th>Symbology & Prefix</th>
                  <th>Check Digit</th>
                  <th>Label Size</th>
                  <th>Copies</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredConfigs.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
                      No barcode rules match your search criteria.
                    </td>
                  </tr>
                ) : (
                  pagination.pagedItems.map((item) => (
                    <tr
                      key={item.ruleID || item.id || item.ruleCode}
                      onClick={() => setPreviewItem(item)}
                      style={{
                        cursor: 'pointer',
                        background: (previewItem?.ruleID === item.ruleID || previewItem?.ruleCode === item.ruleCode) ? '#f0f9ff' : 'inherit'
                      }}
                    >
                      <td>
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>{item.ruleName}</div>
                        <small style={{ color: '#64748b', fontFamily: 'monospace' }}>{item.ruleCode || `BC-${item.ruleID}`}</small>
                      </td>
                      <td>
                        <span className="micro-badge micro-badge-info" style={{ fontSize: '11px' }}>
                          {item.centreName || 'All Centers'}
                        </span>
                      </td>
                      <td>
                        <span style={{ fontSize: '12px', fontWeight: 500 }}>{item.specimenType}</span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <strong style={{ fontSize: '12px', color: '#0284c7' }}>{item.symbology}</strong>
                          <small style={{ color: '#475569', fontFamily: 'monospace' }}>Prefix: {item.prefix}-XXXX</small>
                        </div>
                      </td>
                      <td>
                        <span style={{ fontSize: '12px' }}>{item.checkDigit}</span>
                      </td>
                      <td>
                        <span style={{ fontSize: '12px', fontWeight: 500 }}>{item.labelSize} mm</span>
                      </td>
                      <td>
                        <span style={{ fontSize: '12px' }}>{item.copies}x</span>
                      </td>
                      <td>
                        <span
                          className={`micro-badge ${item.isActive ? 'micro-badge-success' : 'micro-badge-warning'}`}
                          style={{ cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleStatus(item);
                          }}
                        >
                          {item.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                          <button
                            className="micro-btn-icon"
                            title="Edit Rule"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenEdit(item);
                            }}
                          >
                            ✏️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
          <CommonPagination {...pagination.paginationProps} />
        </div>

        {/* Right: Live Label Simulator */}
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🖨️</span> Live Label Simulation
          </h3>
          {previewItem ? (
            <div>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>
                Rule: <strong>{previewItem.ruleName}</strong> ({previewItem.labelSize} mm)
              </div>

              {/* Physical Label Mock */}
              <div
                style={{
                  background: '#fafafa',
                  border: '2px dashed #94a3b8',
                  borderRadius: '6px',
                  padding: '12px',
                  position: 'relative',
                  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                  minHeight: '160px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}
              >
                {/* Label Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px' }}>
                  <div>
                    <div style={{ fontSize: '10px', fontWeight: 700, color: '#0f172a', textTransform: 'uppercase' }}>
                      {previewItem.centreName || 'CENTRAL HOSPITAL LAB'}
                    </div>
                    <div style={{ fontSize: '11px', fontWeight: 800, color: '#0284c7', fontFamily: 'monospace' }}>
                      {formatBarcodeWithCheckDigit(previewItem.prefix, '2026-08492', previewItem.checkDigit)}
                    </div>
                  </div>
                  {previewItem.includeBiohazard && (
                    <span style={{ fontSize: '14px', color: '#ef4444' }} title="Biohazard">☣️</span>
                  )}
                </div>

                {/* Simulated Barcode Pattern */}
                <div style={{ margin: '8px 0', textAlign: 'center', background: '#fff', padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px' }}>
                  {previewItem.symbology === 'DATAMATRIX_2D' || previewItem.symbology === 'QR_CODE' ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <div style={{ width: '48px', height: '48px', background: '#0f172a', display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '2px', padding: '3px' }}>
                        {Array.from({ length: 36 }).map((_, i) => (
                          <div key={i} style={{ background: (i % 2 === 0 || i % 3 === 0) ? '#fff' : '#0f172a' }} />
                        ))}
                      </div>
                      <div style={{ textAlign: 'left', fontSize: '10px', fontFamily: 'monospace' }}>
                        <div><strong>2D MATRIX</strong></div>
                        <div>{formatBarcodeWithCheckDigit(previewItem.prefix, '08492', previewItem.checkDigit)}</div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ display: 'flex', height: '36px', justifyContent: 'center', gap: '2px', alignItems: 'stretch' }}>
                        {[2,1,3,1,2,4,1,2,3,1,2,1,4,2,1,3,2,1,3,1,2,4,1,2].map((w, idx) => (
                          <div key={idx} style={{ width: `${w * 2}px`, background: idx % 2 === 0 ? '#0f172a' : 'transparent' }} />
                        ))}
                      </div>
                      <div style={{ fontSize: '9px', fontFamily: 'monospace', marginTop: '2px', color: '#334155' }}>
                        *{formatBarcodeWithCheckDigit(previewItem.prefix, '202608492', previewItem.checkDigit)}*
                      </div>
                    </div>
                  )}
                </div>

                {/* Human Readable Metadata */}
                <div style={{ fontSize: '9px', color: '#1e293b', lineHeight: 1.3 }}>
                  <div><strong>PT:</strong> DOE, JOHN (45Y / M)</div>
                  <div><strong>SPEC:</strong> {previewItem.specimenType === 'ALL' ? 'Urine Clean Catch' : previewItem.specimenType}</div>
                  <div><strong>COLL:</strong> 26-Sep-2026 09:30 | WARD: ICU-03</div>
                  {previewItem.includeIsolateSuffix && (
                    <div style={{ color: '#b91c1c', fontWeight: 700 }}>ISOLATE: #1 (E. coli)</div>
                  )}
                </div>
              </div>

              {/* Hardware Config specs */}
              <div style={{ marginTop: '12px', fontSize: '11px', color: '#475569', background: '#f8fafc', padding: '8px', borderRadius: '6px' }}>
                <div><strong>Printer:</strong> {previewItem.printerType} ({previewItem.printerDpi})</div>
                <div><strong>Copies:</strong> {previewItem.copies} label(s) per accession</div>
                <div><strong>Check Digit:</strong> {previewItem.checkDigit} {previewItem.checkDigit !== 'NONE' ? `(Calc: ${calculateCheckDigit('202608492', previewItem.checkDigit)})` : ''}</div>
              </div>
            </div>
          ) : (
            <p style={{ color: '#64748b', fontSize: '12px' }}>Select a rule to preview</p>
          )}
        </div>
      </div>

      {/* Modal: Create/Edit Barcode Rule */}
      {showModal && (
        <div className="micro-modal-backdrop">
          <div className="micro-modal" style={{ maxWidth: '680px', width: '90%' }}>
            <div className="micro-modal-header">
              <h2>{editingConfig ? 'Edit Barcode & Label Rule' : 'Create New Barcode & Label Rule'}</h2>
              <button className="micro-modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>

            <form onSubmit={handleSaveConfig}>
              <div className="micro-modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div style={{ gridColumn: 'span 2' }}>
                  <label className="micro-label">Rule Name *</label>
                  <input
                    type="text"
                    className="micro-input"
                    required
                    placeholder="e.g., Blood Culture Automated Accession Label"
                    value={formData.ruleName}
                    onChange={(e) => setFormData({ ...formData, ruleName: e.target.value })}
                  />
                </div>

                <div>
                  <label className="micro-label">Center / Laboratory Scope</label>
                  <select
                    className="micro-input"
                    value={formData.centreID || 'ALL'}
                    onChange={(e) => {
                      const val = e.target.value === 'ALL' ? null : parseInt(e.target.value);
                      const text = e.target.options[e.target.selectedIndex].text;
                      setFormData({ ...formData, centreID: val, centreName: text });
                    }}
                  >
                    <option value="ALL">All Centers / Central Lab</option>
                    <option value="1">Main Hospital Central Lab</option>
                    <option value="2">Tertiary Reference Laboratory</option>
                    <option value="3">North Satellite Branch</option>
                  </select>
                </div>

                <div>
                  <label className="micro-label">Specimen Matrix</label>
                  <select
                    className="micro-input"
                    value={formData.specimenType}
                    onChange={(e) => setFormData({ ...formData, specimenType: e.target.value })}
                  >
                    <option value="ALL">All Specimen Types</option>
                    <option value="Blood">Blood (Aerobic/Anaerobic)</option>
                    <option value="Urine">Urine (Midstream / Catheter)</option>
                    <option value="Sputum">Sputum / BAL</option>
                    <option value="CSF">CSF / Sterile Fluids</option>
                    <option value="Pus / Swab">Pus / Wound Swab</option>
                    <option value="Isolate Stock">Isolate Subculture Stock</option>
                  </select>
                </div>

                <div>
                  <label className="micro-label">Barcode Symbology *</label>
                  <select
                    className="micro-input"
                    value={formData.symbology}
                    onChange={(e) => setFormData({ ...formData, symbology: e.target.value })}
                  >
                    {SYMBOLOGY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="micro-label">Check Digit Algorithm</label>
                  <select
                    className="micro-input"
                    value={formData.checkDigit}
                    onChange={(e) => setFormData({ ...formData, checkDigit: e.target.value })}
                  >
                    {CHECK_DIGIT_ALGORITHMS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="micro-label">Barcode Prefix</label>
                  <input
                    type="text"
                    className="micro-input"
                    placeholder="e.g., MB, BC, ISO"
                    value={formData.prefix}
                    onChange={(e) => setFormData({ ...formData, prefix: e.target.value.toUpperCase() })}
                  />
                </div>

                <div>
                  <label className="micro-label">Sequence Length</label>
                  <input
                    type="number"
                    min="4"
                    max="12"
                    className="micro-input"
                    value={formData.sequenceLength}
                    onChange={(e) => setFormData({ ...formData, sequenceLength: parseInt(e.target.value) || 8 })}
                  />
                </div>

                <div>
                  <label className="micro-label">Label Size (Width x Height)</label>
                  <select
                    className="micro-input"
                    value={formData.labelSize}
                    onChange={(e) => setFormData({ ...formData, labelSize: e.target.value })}
                  >
                    {LABEL_SIZES.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="micro-label">Number of Copies</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    className="micro-input"
                    value={formData.copies}
                    onChange={(e) => setFormData({ ...formData, copies: parseInt(e.target.value) || 1 })}
                  />
                </div>

                <div style={{ gridColumn: 'span 2' }}>
                  <label className="micro-label">Human-Readable Fields to Print</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px', background: '#f8fafc', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                    {AVAILABLE_HR_FIELDS.map(f => (
                      <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={(formData.humanReadableFields || []).includes(f.id)}
                          onChange={() => handleToggleHRField(f.id)}
                        />
                        {f.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', marginTop: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.includeBiohazard}
                      onChange={(e) => setFormData({ ...formData, includeBiohazard: e.target.checked })}
                    />
                    Include Biohazard Symbol (☣️)
                  </label>
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer', marginTop: '8px' }}>
                    <input
                      type="checkbox"
                      checked={formData.includeIsolateSuffix}
                      onChange={(e) => setFormData({ ...formData, includeIsolateSuffix: e.target.checked })}
                    />
                    Include Isolate Subculture Suffix
                  </label>
                </div>
              </div>

              <div className="micro-modal-footer">
                <button type="button" className="micro-btn-secondary" onClick={() => setShowModal(false)} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="micro-btn-primary" disabled={saving}>
                  {saving ? 'Saving to Database...' : 'Save Barcode Rule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
